/**
 * Supabase Storage image transforms.
 *
 * Swaps `/storage/v1/object/{public|sign}/…` for the render endpoint
 * (`/storage/v1/render/image/{public|sign}/…`) and appends width/quality
 * params. The render endpoint negotiates WebP via the browser `Accept`
 * header automatically, so we don't need to force a format.
 *
 * Non-Supabase URLs or already-transformed URLs are returned unchanged.
 */
export type ImageTransformOpts = {
  width?: number;
  height?: number;
  quality?: number;
  resize?: "cover" | "contain" | "fill";
  format?: "origin" | "webp" | "avif";
};

export function transformSupabaseImage(
  url: string | null | undefined,
  opts: ImageTransformOpts = {},
): string {
  if (!url) return "";
  if (!/\/storage\/v1\/object\/(public|sign)\//.test(url)) return url;
  if (/\/storage\/v1\/render\/image\//.test(url)) return url;
  const rendered = url.replace(
    /\/storage\/v1\/object\/(public|sign)\//,
    "/storage/v1/render/image/$1/",
  );
  const u = new URL(rendered);
  const { width, height, quality = 78, resize = "contain", format } = opts;
  if (width) u.searchParams.set("width", String(width));
  if (height) u.searchParams.set("height", String(height));
  u.searchParams.set("quality", String(quality));
  u.searchParams.set("resize", resize);
  if (format) u.searchParams.set("format", format);
  return u.toString();
}

/**
 * Build a responsive srcSet for an auction hero image using Supabase
 * render transforms. Widths chosen to cover common mobile/tablet/desktop
 * DPR combinations without over-serving. Rendered slot is ~360–480 CSS px.
 */
export function auctionHeroSrcSet(
  url: string | null | undefined,
  format?: "webp" | "avif",
): string {
  if (!url) return "";
  const widths = [360, 480, 720, 1080, 1440];
  return widths
    .map(
      (w) =>
        `${transformSupabaseImage(url, { width: w, quality: 72, format })} ${w}w`,
    )
    .join(", ");
}

